import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Chip, CircularProgress, Link, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DashboardService } from '../../../services/dashboard.service';
import { TeamToday as TeamTodayData } from '../../../interfaces/dashboard.interface';
import StatTile from '../charts/StatTile';
import HStack100 from '../charts/HStack100';
import Columns from '../charts/Columns';
import { DashSection, DashEmpty } from './DashSection';
import { formatDay } from './personalDashboardUtils';

interface Props {
    institutionId: string;
    /** Where a pending request links to; null renders the name as plain text. */
    approvalsPath: string | null;
}

const NOT_IN_SHOWN = 8;

/**
 * "My team — today" for department heads. The server decides who is in the team (active members
 * of the departments the caller heads, the caller excluded) and 403s anyone who heads none, in
 * which case the block renders nothing.
 */
const TeamToday: React.FC<Props> = ({ institutionId, approvalsPath }) => {
    const { t } = useTranslation('dashboard');
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<TeamTodayData | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        DashboardService.getMyTeamToday(institutionId)
            .then(d => { if (!cancelled) setData(d); })
            .catch(() => { if (!cancelled) setData(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [institutionId]);

    const stats = useMemo(() => {
        if (!data) return null;
        const by = (s: string) => data.members.filter(m => m.status === s);
        const onTime = by('ON_TIME').length;
        const late = by('LATE').length;
        const hours = new Map<number, number>();
        data.members.forEach(m => {
            if (!m.firstIn) return;
            const h = new Date(m.firstIn).getHours();
            hours.set(h, (hours.get(h) ?? 0) + 1);
        });
        const arrivals = hours.size === 0 ? [] : (() => {
            const keys = Array.from(hours.keys());
            const lo = Math.min(...keys), hi = Math.max(...keys);
            return Array.from({ length: hi - lo + 1 }, (_, i) => ({
                label: `${String(lo + i).padStart(2, '0')}:00`, value: hours.get(lo + i) ?? 0,
            }));
        })();
        return {
            size: data.members.length, present: onTime + late, onTime, late,
            leave: by('ON_LEAVE').length, notIn: by('NOT_IN'), arrivals,
        };
    }, [data]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }} role="status" aria-label={t('team.loading')}>
                <CircularProgress size={28} />
            </Box>
        );
    }
    if (!data || !stats || stats.size === 0) return null;

    const pending = data.pendingLeaves;

    return (
        <Box data-testid="team-today" sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>{t('team.title')}</Typography>
            <Paper variant="outlined" sx={{ p: 1, mb: 2 }}>
                <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap' }}>
                    <StatTile label={t('team.teamSize')} value={stats.size} />
                    <StatTile label={t('team.presentToday')} value={stats.present} tone={stats.present > 0 ? 'good' : 'default'} />
                    <StatTile label={t('team.lateToday')} value={stats.late} tone={stats.late > 0 ? 'warning' : 'default'} />
                    <StatTile label={t('team.onLeaveToday')} value={stats.leave} />
                    <StatTile
                        label={t('team.notInYet')} value={stats.notIn.length}
                        tone={stats.notIn.length > 0 ? 'warning' : 'good'} sub={t('team.notInSub')}
                    />
                    {pending && (
                        <StatTile
                            label={t('team.leaveApprovals')} value={pending.length}
                            tone={pending.length > 0 ? 'warning' : 'default'} sub={t('team.awaitingDecision')}
                        />
                    )}
                </Stack>
            </Paper>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(2, minmax(0,1fr))' }, gap: 2 }}>
                <DashSection title={t('team.teamToday')} subtitle={t('team.teamTodaySub')}>
                    <HStack100
                        rows={[{ label: t('team.teamRow'), segments: [stats.onTime, stats.late, stats.leave, stats.notIn.length] }]}
                        seriesLabels={[t('team.onTime'), t('team.late'), t('team.onLeave'), t('team.notInYet')]}
                        colorIndices={[2, 3, 0, 7]}
                    />
                </DashSection>
                {stats.arrivals.length > 0 && (
                    <DashSection title={t('team.arrivals')} subtitle={t('team.arrivalsSub')}>
                        <Columns data={stats.arrivals} yFmt={n => (Number.isInteger(n) ? String(n) : '')} />
                    </DashSection>
                )}
                <DashSection title={t('team.notInYet')} subtitle={t('team.notInSectionSub')}>
                    {stats.notIn.length === 0 ? <DashEmpty text={t('team.everyoneIn')} /> : (
                        <Stack spacing={0.5} data-testid="team-not-in">
                            {stats.notIn.slice(0, NOT_IN_SHOWN).map(m => (
                                <Typography key={m.userId} variant="body2">{m.name || t('team.teamMember')}</Typography>
                            ))}
                            {stats.notIn.length > NOT_IN_SHOWN && (
                                <Typography variant="caption" color="text.secondary">
                                    {t('team.more', { n: stats.notIn.length - NOT_IN_SHOWN })}
                                </Typography>
                            )}
                        </Stack>
                    )}
                </DashSection>
                {pending && (
                    <DashSection title={t('team.pendingTitle')} subtitle={t('team.pendingSub')}>
                        {pending.length === 0 ? <DashEmpty text={t('team.noPending')} /> : (
                            <Stack spacing={1} divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />} data-testid="team-pending">
                                {pending.slice(0, 5).map(l => (
                                    <Stack key={l.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                        {approvalsPath
                                            ? (
                                                <Link component={RouterLink} to={approvalsPath} underline="hover" sx={{ fontWeight: 600 }}>
                                                    {l.name || t('team.teamMember')}
                                                </Link>
                                            )
                                            : <Typography variant="body2" sx={{ fontWeight: 600 }}>{l.name || t('team.teamMember')}</Typography>}
                                        <Chip size="small" label={l.leaveType} />
                                        <Box sx={{ flex: 1 }} />
                                        <Typography variant="body2">
                                            {t('personal.dateRange', { from: formatDay(l.startDate), to: formatDay(l.endDate) })}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                    </DashSection>
                )}
            </Box>
        </Box>
    );
};

export default TeamToday;
